import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Card, Col, Container, Form, ListGroup, Modal, Row, Spinner, Tab, Tabs } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  driverLinkLogin,
  getDriverTrip,
  listDriverTrips,
  markDriverTracking,
  uploadOdometerPhoto,
  saveDriverReadinessGps,
  saveDriverReadinessPractice,
  syncDriverLocations,
} from "../services/tripTrackingApi";
import { formatDateDMY, formatDateTimeDMY12 } from "../utils/dateFormat";
import useDriverGpsTracking from "../hooks/useDriverGpsTracking";
import useLocationGate from "../hooks/useLocationGate";
import OdometerCaptureModal from "../components/OdometerCaptureModal";
import {
  newPhotoId,
  readPendingPhoto,
  removePendingPhoto,
  savePendingPhoto,
} from "../services/driverPhotoQueue";
import {
  flushPendingEvents,
  getAllScopeKeys,
  getPendingEvents,
  queueEvent,
  removePendingEvent,
  toOptimisticEvent,
} from "../services/driverOfflineQueue";

const PENDING_SYNC_RETRY_MS = 20000;

// Only the trip's start and end need speedometer proof; every other checkbox stays a single tap.
const ODOMETER_EVENT_TYPES = ["TRIP_STARTED", "TRIP_ENDED"];

const TRIP_TABS = [
  ["upcoming", "Upcoming"],
  ["ongoing", "Ongoing"],
  ["completed", "Completed"],
];

export default function DriverPortal() {
  const { accessKey } = useParams();
  const storageKey = accessKey ? `driverSession:${accessKey}` : "driverSession";
  const [driver, setDriver] = useState(() => readDriverSession(storageKey));
  const [loginBusy, setLoginBusy] = useState(false);
  const [trips, setTrips] = useState({ upcoming: [], ongoing: [], completed: [] });
  const [loadingTrips, setLoadingTrips] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [marking, setMarking] = useState("");
  const [readinessBusy, setReadinessBusy] = useState("");

  const driverId = driver?.driverMasterId || driver?.driverId;

  useEffect(() => {
    setDriver(readDriverSession(storageKey));
    setTrips({ upcoming: [], ongoing: [], completed: [] });
    setDetail(null);
    setSelectedBookingId(null);
  }, [storageKey]);

  useEffect(() => {
    if (!accessKey) return;
    if (driver?.accessKey === accessKey && driverId) return;
    let cancelled = false;
    setLoginBusy(true);
    driverLinkLogin(accessKey)
      .then((data) => {
        if (cancelled) return;
        if (!data?.ok) throw new Error("Login failed");
        localStorage.setItem(storageKey, JSON.stringify(data));
        localStorage.removeItem("driverSession");
        setDriver(data);
      })
      .catch((error) => {
        if (!cancelled) {
          toast.error(error?.response?.data?.error || "Driver portal link is invalid or inactive");
          setDriver(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoginBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accessKey, driver?.accessKey, driverId, storageKey]);

  const loadTrips = useCallback(async () => {
    if (!driverId) return;
    setLoadingTrips(true);
    try {
      const data = await listDriverTrips(driverId);
      setTrips(data);
      const first = data.ongoing?.[0] || data.upcoming?.[0] || data.completed?.[0] || null;
      setSelectedBookingId((current) => current || first?.bookingId || null);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not load assigned trips");
    } finally {
      setLoadingTrips(false);
    }
  }, [driverId]);

  useEffect(() => {
    loadTrips();
  }, [loadTrips]);

  const scopeKey = useCallback((bookingId) => `driver:${driverId}:${bookingId}`, [driverId]);

  // The photo has to reach S3 before the tracking event that references it. Doing that here means
  // the live tap and the offline drain share exactly one code path.
  const sendTrackingAction = useCallback(
    async (bookingId, item) => {
      let odometerPhotoUrl = item.odometerPhotoUrl || "";
      if (!odometerPhotoUrl && item.odometerPhotoId) {
        const blob = pendingPhotoBlobsRef.current.get(item.odometerPhotoId)
          || await readPendingPhoto(item.odometerPhotoId).catch(() => null);
        if (blob) {
          odometerPhotoUrl = await uploadOdometerPhoto({
            driverId,
            bookingId,
            eventType: item.eventType,
            blob,
          });
        }
      }
      const event = await markDriverTracking({
        driverId,
        bookingId,
        eventType: item.eventType,
        placeKey: item.placeKey,
        placeLabel: item.placeLabel,
        clientCapturedAt: item.clientCapturedAt,
        latitude: item.latitude,
        longitude: item.longitude,
        accuracy: item.accuracy,
        gpsProofType: item.gpsProofType,
        gpsCapturedAt: item.gpsCapturedAt,
        odometerReading: item.odometerReading,
        odometerPhotoUrl: odometerPhotoUrl || undefined,
      });
      if (item.odometerPhotoId) {
        pendingPhotoBlobsRef.current.delete(item.odometerPhotoId);
        await removePendingPhoto(item.odometerPhotoId).catch(() => null);
      }
      return event;
    },
    [driverId],
  );

  // Resends anything queued offline for one booking; leaves items queued on repeat failure.
  const syncPending = useCallback(
    async (bookingId) => {
      if (!driverId || !bookingId) return;
      await flushPendingEvents(
        scopeKey(bookingId),
        (item) => sendTrackingAction(bookingId, item),
        (event) => {
          if (bookingId === selectedBookingId) {
            setDetail((current) => applyEvent(current, event));
          }
          if (event.eventType === "TRIP_STARTED") {
            setTrips((current) => moveTripToOngoing(current, bookingId));
          }
        },
        { captureGpsProof: captureEventLocation }
      );
    },
    [driverId, scopeKey, selectedBookingId, sendTrackingAction]
  );

  // Drains queued actions for every trip this driver has pending, not just the one on screen -
  // connectivity can return while a different trip is selected.
  const syncAllPending = useCallback(async () => {
    if (!driverId) return;
    const prefix = `driver:${driverId}:`;
    const bookingIds = getAllScopeKeys()
      .filter((key) => key.startsWith(prefix))
      .map((key) => Number(key.slice(prefix.length)))
      .filter((id) => Number.isFinite(id));
    for (const bookingId of bookingIds) {
      await syncPending(bookingId);
    }
  }, [driverId, syncPending]);

  useEffect(() => {
    if (!driverId) return undefined;
    syncAllPending();
    window.addEventListener("online", syncAllPending);
    const interval = setInterval(syncAllPending, PENDING_SYNC_RETRY_MS);
    return () => {
      window.removeEventListener("online", syncAllPending);
      clearInterval(interval);
    };
  }, [driverId, syncAllPending]);

  useEffect(() => {
    const loadDetail = async () => {
      if (!driverId || !selectedBookingId) {
        setDetail(null);
        return;
      }
      setDetailLoading(true);
      try {
        const data = await getDriverTrip(driverId, selectedBookingId);
        const pending = getPendingEvents(scopeKey(selectedBookingId));
        setDetail(pending.reduce((current, item) => applyEvent(current, toOptimisticEvent(item)), data));
        if (pending.length) syncPending(selectedBookingId);
      } catch (error) {
        toast.error(error?.response?.data?.message || "Could not load trip details");
      } finally {
        setDetailLoading(false);
      }
    };
    loadDetail();
  }, [driverId, selectedBookingId, scopeKey, syncPending]);

  const selectedTrip = useMemo(() => {
    const all = [...(trips.upcoming || []), ...(trips.ongoing || []), ...(trips.completed || [])];
    return all.find((trip) => trip.bookingId === selectedBookingId) || null;
  }, [trips, selectedBookingId]);

  const gpsStatus = useDriverGpsTracking({
    detail,
    scopeKey: driverId && selectedBookingId ? `gps:driver:${driverId}:${selectedBookingId}` : "",
    sendBatch: useCallback(
      (pings) => syncDriverLocations({ driverId, bookingId: selectedBookingId, pings }),
      [driverId, selectedBookingId],
    ),
  });
  const installPrompt = usePwaInstallPrompt();
  const locationGate = useLocationGate();
  // Resolved by the capture modal, so mark() can simply await the driver's photo and reading.
  const [odometerRequest, setOdometerRequest] = useState(null);
  // IndexedDB is unavailable in some private-browsing modes. Keeping the blob in memory as well
  // means the immediate upload still works there; only the offline replay is lost.
  const pendingPhotoBlobsRef = useRef(new Map());
  const requestOdometer = useCallback(
    (eventLabel) => new Promise((resolve) => setOdometerRequest({ eventLabel, resolve })),
    [],
  );
  // The gate blocks trip actions, not the trip information: a driver mid-trip still needs the
  // hotel and caretaker numbers even when location is off.
  const [locationGateDismissed, setLocationGateDismissed] = useState(false);
  useEffect(() => {
    setLocationGateDismissed(false);
  }, [selectedBookingId]);
  const showLocationGate = Boolean(detail)
    && !locationGate.ready
    && locationGate.state !== "checking"
    && !locationGateDismissed;
  // Re-opens the gate with an up to date reason whenever a capture could not get a fix.
  const requireLocation = useCallback(async () => {
    const next = await locationGate.refresh();
    if (next !== "granted") setLocationGateDismissed(false);
    return next === "granted";
  }, [locationGate]);

  const updateReadiness = (readiness) => {
    setDetail((current) => (current ? { ...current, driverReadiness: readiness } : current));
  };

  const testReadinessGps = async () => {
    if (!driverId || !selectedBookingId) return;
    setReadinessBusy("gps");
    try {
      const location = await captureEventLocation("EXACT");
      if (!hasLocation(location)) {
        await requireLocation();
        return;
      }
      const readiness = await saveDriverReadinessGps({
        driverId,
        bookingId: selectedBookingId,
        ...location,
        clientCapturedAt: location.gpsCapturedAt || new Date().toISOString(),
        deviceInfo: deviceInfo(),
      });
      updateReadiness(readiness);
      toast.success("GPS test completed");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not save GPS test");
    } finally {
      setReadinessBusy("");
    }
  };

  const completeReadinessPractice = async () => {
    if (!driverId || !selectedBookingId) return;
    setReadinessBusy("practice");
    try {
      const readiness = await saveDriverReadinessPractice({
        driverId,
        bookingId: selectedBookingId,
        clientCapturedAt: new Date().toISOString(),
        deviceInfo: deviceInfo(),
      });
      updateReadiness(readiness);
      toast.success("Practice checkbox completed");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Complete GPS test first");
    } finally {
      setReadinessBusy("");
    }
  };

  const logout = () => {
    localStorage.removeItem(storageKey);
    setDriver(null);
    setTrips({ upcoming: [], ongoing: [], completed: [] });
    setDetail(null);
    setSelectedBookingId(null);
  };

  const mark = async ({ eventType, placeKey, placeLabel }) => {
    if (!driverId || !selectedBookingId) return;
    const bookingId = selectedBookingId;
    const key = `${eventType}:${placeKey}`;
    const location = await captureEventLocation("EXACT");
    if (!hasLocation(location)) {
      await requireLocation();
      return;
    }
    let odometer = null;
    if (ODOMETER_EVENT_TYPES.includes(eventType)) {
      odometer = await requestOdometer(eventType === "TRIP_STARTED" ? "Trip started" : "Trip ended");
      if (!odometer) return;
    }

    const action = { eventType, placeKey, placeLabel, clientCapturedAt: new Date().toISOString(), ...location };
    if (odometer) {
      const photoId = newPhotoId(scopeKey(bookingId), eventType);
      pendingPhotoBlobsRef.current.set(photoId, odometer.blob);
      await savePendingPhoto(scopeKey(bookingId), photoId, odometer.blob).catch(() => null);
      action.odometerReading = odometer.reading;
      action.odometerPhotoId = photoId;
    }

    // Capture the tap and reflect it right away - this must not depend on connectivity.
    setDetail((current) => applyEvent(current, toOptimisticEvent(action)));
    queueEvent(scopeKey(bookingId), action);
    if (eventType === "TRIP_STARTED") {
      setTrips((current) => moveTripToOngoing(current, bookingId));
    }

    setMarking(key);
    try {
      const event = await sendTrackingAction(bookingId, action);
      removePendingEvent(scopeKey(bookingId), eventType, placeKey);
      setDetail((current) => applyEvent(current, event));
      toast.success("Timestamp captured");
    } catch (error) {
      if (error?.response) {
        toast.error(error.response?.data?.message || "Could not save timestamp");
      } else {
        toast.info("No connection - saved on this device and will sync automatically once you're back online");
      }
    } finally {
      setMarking("");
    }
  };

  if (!driver) {
    return (
      <Container className="driver-login-page py-4 px-3" style={{ maxWidth: 480 }}>
        <Card className="driver-login-card shadow-lg border-0">
          <Card.Body>
            <div className="driver-login-icon"><i className="bi bi-car-front-fill" /></div><h3 className="mb-1 fw-bold">Driver Portal</h3>
            <div className="text-muted small mb-3">
              {accessKey ? "Opening your assigned trips..." : "Use the unique driver portal link shared by the team."}
            </div>
            {loginBusy ? (
              <div className="py-3 text-center"><Spinner /></div>
            ) : null}
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container fluid className="driver-portal-page py-3 px-2 px-sm-3">
      <LocationGateModal
        show={showLocationGate}
        state={locationGate.state}
        busy={locationGate.busy}
        onEnable={locationGate.request}
        onDismiss={() => setLocationGateDismissed(true)}
      />
      <OdometerCaptureModal
        show={Boolean(odometerRequest)}
        eventLabel={odometerRequest?.eventLabel || ""}
        onCancel={() => {
          odometerRequest?.resolve(null);
          setOdometerRequest(null);
        }}
        onConfirm={(result) => {
          odometerRequest?.resolve(result);
          setOdometerRequest(null);
        }}
      />
      <Row className="driver-portal-header align-items-center g-2 mb-3">
        <Col>
          <h3 className="mb-0 fw-bold"><i className="bi bi-car-front-fill me-2" />Driver Portal</h3>
          <div className="text-muted small">{driver.name || driver.username} {driver.phone ? `- ${driver.phone}` : ""}</div>
        </Col>
        <Col xs="auto" className="d-flex gap-2">
          <Button variant="outline-secondary" size="sm" onClick={loadTrips}>Refresh</Button>
          {!accessKey && <Button variant="outline-danger" size="sm" onClick={logout}>Logout</Button>}
        </Col>
      </Row>

      <Row className="g-3">
        <Col xs={12} lg={4}>
          <Card className="driver-panel shadow-sm border-0">
            <Card.Header className="fw-semibold">Assigned Trips</Card.Header>
            <Card.Body>
              {loadingTrips ? (
                <div className="py-4 text-center"><Spinner /></div>
              ) : (
                <Tabs defaultActiveKey="upcoming" className="mb-3" fill>
                  {TRIP_TABS.map(([key, label]) => (
                    <Tab eventKey={key} title={`${label} (${trips[key]?.length || 0})`} key={key}>
                      <TripList
                        rows={trips[key] || []}
                        selectedBookingId={selectedBookingId}
                        onSelect={setSelectedBookingId}
                      />
                    </Tab>
                  ))}
                </Tabs>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} lg={8}>
          <Card className="driver-panel shadow-sm border-0">
            <Card.Header className="fw-semibold">Trip Details</Card.Header>
            <Card.Body>
              {!selectedBookingId && <div className="text-muted">Select a trip to view details.</div>}
              {selectedBookingId && detailLoading && <div className="py-5 text-center"><Spinner /></div>}
              {selectedBookingId && !detailLoading && detail && (
                <TripDetail
                  detail={detail}
                  selectedTrip={selectedTrip}
                  marking={marking}
                  onMark={mark}
                  gpsStatus={gpsStatus}
                  locationState={locationGate.state}
                  installPrompt={installPrompt}
                  readinessBusy={readinessBusy}
                  onReadinessGps={testReadinessGps}
                  onReadinessPractice={completeReadinessPractice}
                />
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

function TripList({ rows, selectedBookingId, onSelect }) {
  if (!rows.length) return <div className="text-muted small py-3">No trips found.</div>;
  return (
    <ListGroup className="driver-trip-list">
      {rows.map((trip) => (
        <ListGroup.Item
          action
          className="driver-trip-item"
          active={trip.bookingId === selectedBookingId}
          key={trip.bookingId}
          onClick={() => onSelect(trip.bookingId)}
        >
          <div className="d-flex justify-content-between gap-2">
            <span className="fw-semibold">{trip.tripTitle || `Booking #${trip.bookingId}`}</span>
            <Badge bg={statusVariant(trip.tripStatus)}>{trip.tripStatus}</Badge>
          </div>
          <div className="small">Travel: {formatDateDMY(trip.vehicleStartDate || trip.travelDate, "-")}</div>
          <div className="small">
            Customer: {trip.customerName || "-"} {trip.customerPhone ? `| ${trip.customerPhone}` : ""}
          </div>
          {trip.plannedByPhone && (
            <div className="small text-muted">
              Trip planned by: {trip.plannedByName || "-"} | {trip.plannedByPhone}
            </div>
          )}
          <div className="small text-muted">{trip.destination || "-"} {trip.duration ? `- ${trip.duration}` : ""}</div>
        </ListGroup.Item>
      ))}
    </ListGroup>
  );
}

export function TripDetail({
  detail,
  selectedTrip,
  marking,
  onMark,
  gpsStatus,
  locationState,
  installPrompt,
  readinessBusy,
  onReadinessGps,
  onReadinessPractice,
}) {
  const vehicle = detail.vehicle || selectedTrip?.vehicle || null;
  const dayGroups = groupPlacesByDay(detail.places || []);
  const hotelGroups = groupHotelsByDay(detail.dayHotels || []);
  const eventByKey = useMemo(() => buildEventMap(detail), [detail]);
  const lastDayIndex = dayGroups.length - 1;
  const allDaysCompleted = dayGroups.length > 0 && dayGroups.every((day, index) =>
    isDayCompleted(day, eventByKey, index === lastDayIndex)
  );
  return (
    <div className="driver-trip-detail vstack gap-3">
      <div>
        <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
          <h5 className="mb-0">{detail.tripTitle || `Booking #${detail.bookingId}`}</h5>
          <Badge bg={statusVariant(detail.tripStatus)}>{detail.tripStatus}</Badge>
        </div>
        <div className="text-muted small">
          Travel: {formatDateDMY(detail.vehicleStartDate || detail.travelDate, "-")} | Duration: {detail.duration || "-"} | Destination: {detail.destination || "-"}
        </div>
        <div className="small">
          <span className="text-muted">Customer:</span> {detail.customerName || "-"}
          {detail.customerPhone ? <span> | {detail.customerPhone}</span> : null}
        </div>
        {detail.plannedByPhone && (
          <div className="small text-muted">
            Trip planned by: {detail.plannedByName || "-"} | {detail.plannedByPhone}
          </div>
        )}
        <div className="small">
          <span className="text-muted">Preferred languages:</span>{" "}
          {(detail.driverLanguages || []).length ? detail.driverLanguages.join(", ") : "-"}
        </div>
      </div>

      <DriverReadinessCard
        readiness={detail.driverReadiness}
        busy={readinessBusy}
        onTestGps={onReadinessGps}
        onPractice={onReadinessPractice}
      />

      <div className="border rounded p-3">
        <div className="fw-semibold mb-2">Vehicle Details</div>
        {vehicle ? (
          <Row className="g-2 small">
            <Col md={4}><span className="text-muted">Vehicle:</span> {vehicle.name || "-"}</Col>
            <Col md={4}><span className="text-muted">Registration:</span> {vehicle.registrationNumber || "-"}</Col>
            <Col md={4}><span className="text-muted">Category:</span> {vehicle.category || "-"}</Col>
            <Col md={4}><span className="text-muted">Seater:</span> {vehicle.seater || "-"}</Col>
            <Col md={4}><span className="text-muted">AC:</span> {vehicle.ac ? "AC" : "Non AC"}</Col>
          </Row>
        ) : (
          <div className="text-muted small">No vehicle assigned.</div>
        )}
      </div>

      <GpsStatusCard
        status={gpsStatus}
        locationState={locationState}
        tripStarted={Boolean(detail.tripStarted)}
        installPrompt={installPrompt}
      />

      {detail.refreshmentRoom ? (
        <div className="border rounded p-3">
          <div className="fw-semibold mb-2">Refreshment Room</div>
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
            <div>
              <div className="fw-semibold">{detail.refreshmentRoom.name || "Refreshments"}</div>
              {detail.refreshmentRoom.city ? <div className="small text-muted">{detail.refreshmentRoom.city}</div> : null}
              {detail.refreshmentRoom.description ? <div className="small mt-1">{detail.refreshmentRoom.description}</div> : null}
            </div>
            {detail.refreshmentRoom.googleMapLink ? (
              <Button
                as="a"
                href={detail.refreshmentRoom.googleMapLink}
                target="_blank"
                rel="noreferrer"
                size="sm"
                variant="outline-primary"
              >
                Open Map
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}

      {hotelGroups.length ? (
        <div className="border rounded p-3">
          <div className="fw-semibold mb-2">Day Wise Hotel</div>
          <div className="vstack gap-2">
            {hotelGroups.map((group) => (
              <div className="border rounded p-2" key={group.key}>
                <div className="small fw-semibold mb-2">{group.label}</div>
                <div className="vstack gap-2">
                  {group.hotels.map((hotel) => (
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2" key={`${group.key}-${hotel.hotelId || hotel.hotelName}`}>
                      <div>
                        <div className="fw-semibold">{hotel.hotelName || "-"}</div>
                        <div className="small text-muted">
                          {[hotel.hotelCode, hotel.city].filter(Boolean).join(" - ") || "Hotel details"}
                        </div>
                        {hotel.caretakerNumber ? (
                          <div className="small">
                            <span className="text-muted">Caretaker:</span> {[hotel.caretakerName, hotel.caretakerNumber].filter(Boolean).join(" - ")}
                          </div>
                        ) : null}
                      </div>
                      {hotel.googleMapLink ? (
                        <Button
                          as="a"
                          href={hotel.googleMapLink}
                          target="_blank"
                          rel="noreferrer"
                          size="sm"
                          variant="outline-primary"
                        >
                          Open Map
                        </Button>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className="border rounded p-3">
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
          <TrackingCheck
            label="Driver Reached the Pickup Location"
            event={detail.driverReachedPickup}
            busy={marking === "DRIVER_REACHED_PICKUP:__PICKUP__"}
            onClick={() => onMark({ eventType: "DRIVER_REACHED_PICKUP", placeKey: "__PICKUP__", placeLabel: "Driver reached the pickup location" })}
          />
          {detail.pickupGoogleMapLink ? (
            <Button
              as="a"
              href={detail.pickupGoogleMapLink}
              target="_blank"
              rel="noreferrer"
              size="sm"
              variant="outline-primary"
            >
              Open Map
            </Button>
          ) : null}
        </div>
      </div>

      <div className="border rounded p-3">
        <TrackingCheck
          label="Trip Started"
          event={detail.tripStarted}
          busy={marking === "TRIP_STARTED:__TRIP__"}
          disabled={!detail.driverReachedPickup}
          disabledText="Capture pickup reached first"
          onClick={() => onMark({ eventType: "TRIP_STARTED", placeKey: "__TRIP__", placeLabel: "Trip started" })}
        />
      </div>

      {dayGroups.length ? (
        dayGroups.map((day, index) => (
          <DayTrackingSection
            day={day}
            eventByKey={eventByKey}
            isFirstDay={index === 0}
            isLastDay={index === lastDayIndex}
            key={day.key}
            marking={marking}
            onMark={onMark}
            previousCompleted={index === 0 ? Boolean(detail.tripStarted) : isDayCompleted(dayGroups[index - 1], eventByKey, false)}
          />
        ))
      ) : (
        <div className="border rounded p-3 text-muted small">No Day-Wise Plan locations configured for this trip.</div>
      )}

      <div className="border rounded p-3">
        <TrackingCheck
          label="Trip Ended"
          event={detail.tripEnded}
          busy={marking === "TRIP_ENDED:__TRIP__"}
          disabled={!allDaysCompleted}
          disabledText="Complete all day-wise places first"
          onClick={() => onMark({ eventType: "TRIP_ENDED", placeKey: "__TRIP__", placeLabel: "Trip ended" })}
        />
      </div>
    </div>
  );
}

function GpsStatusCard({ status, locationState, tripStarted, installPrompt }) {
  if (!status) return null;
  const permissionNeedsAttention = Boolean(locationState) && locationState !== "checking" && locationState !== "granted";
  const variant = status.active ? "success" : status.pendingCount ? "warning" : permissionNeedsAttention ? "danger" : "secondary";
  return (
    <div className={`border rounded p-3 ${permissionNeedsAttention && !tripStarted ? "border-warning bg-warning-subtle" : ""}`}>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div>
          <div className="fw-semibold">GPS</div>
          <div className={`small fw-semibold ${gpsStatusClass(status, locationState)}`}>
            {gpsShortStatus(status, locationState)}
          </div>
          {status.error ? <div className="small text-danger">{status.error}</div> : null}
          {status.lastSyncedAt ? (
            <div className="small text-muted">Synced: {formatDateTimeDMY12(status.lastSyncedAt, "-")}</div>
          ) : null}
        </div>
        <div className="d-flex flex-wrap gap-2 align-items-center">
          {installPrompt?.available ? (
            <Button size="sm" variant="outline-secondary" onClick={installPrompt.install}>
              Install App
            </Button>
          ) : null}
          <Badge bg={variant}>{status.pendingCount ? `${status.pendingCount} saved` : status.active ? "ON" : "Paused"}</Badge>
        </div>
      </div>
    </div>
  );
}

// A permission problem always wins over the tracking window: the card used to show "GPS OK" and
// "Allow Location" at the same time because the window was checked first.
function gpsShortStatus(status, locationState) {
  if (locationState === "unsupported") return "GPS Not Supported";
  if (locationState === "insecure") return "Insecure Connection";
  if (locationState === "denied") return "Location Blocked";
  if (locationState === "prompt") return "Turn ON Location";
  if (locationState === "unavailable") return "No GPS Fix";
  if (status.pendingCount) return "Saved Offline";
  if (status.active) return "GPS OK";
  return "GPS Paused";
}

function gpsStatusClass(status, locationState) {
  if (locationState === "unsupported" || locationState === "insecure" || locationState === "denied") {
    return "text-danger";
  }
  if (locationState === "prompt" || locationState === "unavailable" || status.pendingCount) return "text-warning";
  if (status.active) return "text-success";
  return "text-muted";
}

// Each state needs its own wording because a different action fixes it, and only the first one
// can be fixed by a single tap - the rest need the driver to change a setting and come back.
const LOCATION_GATE_COPY = {
  prompt: {
    title: "Location permission not enabled",
    body: "Allow location so your pickup, stops and drop can be verified during the trip.",
    action: "Enable location",
  },
  denied: {
    title: "Location is blocked for this site",
    body: "Your browser is blocking location for iotrips.in. Tap the icon to the left of the address bar, open Permissions, then set Location to Allow.",
    action: "I have allowed it - Retry",
  },
  unavailable: {
    title: "Turn on device location",
    body: "Location is allowed for this site, but your phone's location service is off. Open quick settings, turn on Location, then come back to this page.",
    action: "Retry",
  },
  unsupported: {
    title: "Location is not available",
    body: "This browser cannot provide GPS. Open the trip link in Chrome to continue.",
    action: "Retry",
  },
  insecure: {
    title: "Open the secure link",
    body: "Location only works over a secure connection. Open the portal from https://www.iotrips.in and try again.",
    action: "Retry",
  },
};

function LocationGateModal({ show, state, busy, onEnable, onDismiss }) {
  const copy = LOCATION_GATE_COPY[state] || LOCATION_GATE_COPY.prompt;
  return (
    <Modal show={show} centered backdrop="static" keyboard={false} onHide={onDismiss}>
      <Modal.Body className="text-center px-4 py-4">
        <BlockedLocationIcon />
        <div className="h5 mt-3 mb-2">{copy.title}</div>
        <div className="small text-muted mb-4">{copy.body}</div>
        <div className="d-grid gap-2">
          <Button variant="success" size="lg" onClick={onEnable} disabled={busy}>
            {busy ? "Checking..." : copy.action}
          </Button>
          <Button variant="link" className="text-decoration-none" onClick={onDismiss}>
            View trip details only
          </Button>
        </div>
        <div className="small text-muted mt-3">
          Trip actions stay locked until location is on.
        </div>
      </Modal.Body>
    </Modal>
  );
}

function BlockedLocationIcon() {
  return (
    <svg width="72" height="72" viewBox="0 0 24 24" role="img" aria-label="Location off" fill="none">
      <path
        d="M12 21s7-6.2 7-11a7 7 0 1 0-14 0c0 4.8 7 11 7 11Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="10" r="2.6" stroke="currentColor" strokeWidth="1.6" />
      <path d="M3.5 3.5 20.5 20.5" stroke="#dc3545" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

function DriverReadinessCard({ readiness, busy, onTestGps, onPractice }) {
  const gpsDone = Boolean(readiness?.gpsTestDone);
  const practiceDone = Boolean(readiness?.practiceCheckDone);
  const ready = gpsDone && practiceDone;
  return (
    <div className={`border rounded p-3 ${ready ? "border-success bg-success-subtle" : "border-warning bg-warning-subtle"}`}>
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
        <div>
          <div className="fw-semibold">Pre-Trip Check</div>
          <div className="small text-muted">
            Test GPS and practice before trip.
          </div>
          {gpsDone ? (
            <div className="small text-success">
              GPS test done{readiness?.gpsTestedAt ? `: ${formatDateTimeDMY12(readiness.gpsTestedAt, "-")}` : ""}
              {readiness?.gpsAccuracy ? ` | accuracy ${Math.round(readiness.gpsAccuracy)} m` : ""}
            </div>
          ) : (
            <div className="small text-danger">GPS pending. Turn ON Location.</div>
          )}
          {practiceDone ? (
            <div className="small text-success">
              Practice checkbox done{readiness?.practiceCheckedAt ? `: ${formatDateTimeDMY12(readiness.practiceCheckedAt, "-")}` : ""}
            </div>
          ) : (
            <div className="small text-danger">Practice pending.</div>
          )}
        </div>
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <Button size="sm" variant={gpsDone ? "outline-success" : "primary"} onClick={onTestGps} disabled={busy === "gps"}>
            {busy === "gps" ? "Testing..." : gpsDone ? "Retest GPS" : "Test GPS"}
          </Button>
          <Form.Check
            type="checkbox"
            label={busy === "practice" ? "Saving..." : "Practice"}
            checked={practiceDone}
            disabled={!gpsDone || practiceDone || busy === "practice"}
            onChange={() => {
              if (gpsDone && !practiceDone) onPractice();
            }}
          />
          <Badge bg={ready ? "success" : "warning"} text={ready ? undefined : "dark"}>
            {ready ? "Driver Ready" : "Readiness Pending"}
          </Badge>
        </div>
      </div>
    </div>
  );
}

function DayTrackingSection({ day, eventByKey, isFirstDay, isLastDay, marking, onMark, previousCompleted }) {
  const dayStart = eventByKey.get(`DAY_START:${day.key}`);
  const dayEnd = eventByKey.get(`DAY_END:${day.key}`);
  const dayPlacesCompleted = day.places.every((place) => place.dropped && place.pickedUp);
  const dayCanProceed = isFirstDay ? previousCompleted : Boolean(dayStart);
  return (
    <div className="border rounded">
      <div className="p-3 border-bottom">
        <div className="fw-semibold">{day.label}</div>
        {day.title && <div className="small text-muted">{day.title}</div>}
      </div>
      {!isFirstDay && (
        <div className="p-3 border-bottom">
          <TrackingCheck
            label="Next Day Start"
            event={dayStart}
            busy={marking === `DAY_START:${day.key}`}
            disabled={!previousCompleted}
            disabledText="Complete previous day first"
            onClick={() => onMark({ eventType: "DAY_START", placeKey: day.key, placeLabel: `${day.label} start` })}
          />
        </div>
      )}
      <div className="p-3 border-bottom fw-semibold">Places From Day Wise List</div>
      <div className="vstack">
        {day.places.map((place) => (
          <div className="p-3 border-bottom" key={place.key}>
            <div className="d-flex flex-wrap justify-content-between gap-2 mb-2">
              <div>
                <div className="fw-semibold">{place.label}</div>
                {(place.dayLabel || place.dayTitle) && (
                  <div className="small text-muted">
                    {[place.dayLabel, place.dayTitle].filter(Boolean).join(" - ")}
                  </div>
                )}
              </div>
              {place.googleMapLink ? (
                <Button
                  as="a"
                  href={place.googleMapLink}
                  target="_blank"
                  rel="noreferrer"
                  size="sm"
                  variant="outline-primary"
                >
                  Open Map
                </Button>
              ) : null}
            </div>
            <div className="d-flex flex-wrap gap-3">
              <TrackingCheck
                label="Dropped"
                event={place.dropped}
                busy={marking === `DROPPED:${place.key}`}
                disabled={!dayCanProceed}
                disabledText={isFirstDay ? "Start the trip first" : "Capture next day start first"}
                onClick={() => onMark({ eventType: "DROPPED", placeKey: place.key, placeLabel: place.label })}
              />
              <TrackingCheck
                label="Picked up"
                event={place.pickedUp}
                busy={marking === `PICKED_UP:${place.key}`}
                disabled={!place.dropped}
                disabledText="Capture dropped first"
                onClick={() => onMark({ eventType: "PICKED_UP", placeKey: place.key, placeLabel: place.label })}
              />
            </div>
          </div>
        ))}
      </div>
      {!isLastDay && (
        <div className="p-3">
          <TrackingCheck
            label="Day End"
            event={dayEnd}
            busy={marking === `DAY_END:${day.key}`}
            disabled={!dayPlacesCompleted}
            disabledText="Complete all places for this day first"
            onClick={() => onMark({ eventType: "DAY_END", placeKey: day.key, placeLabel: `${day.label} end` })}
          />
        </div>
      )}
    </div>
  );
}

function TrackingCheck({ label, event, busy, disabled = false, disabledText, onClick }) {
  const checked = Boolean(event);
  const pending = checked && event.pending;
  return (
    <div className={`driver-tracking-action ${checked ? "is-complete" : disabled ? "is-disabled" : "is-ready"} ${pending ? "is-pending-sync" : ""}`}>
      <Form.Check
        className="driver-tracking-check"
        type="checkbox"
        label={label}
        checked={checked}
        disabled={checked || busy || disabled}
        onChange={() => {
          if (!checked && !disabled) onClick();
        }}
      />
      <div className="small text-muted ms-4">
        {checked
          ? `Captured: ${formatDateTimeDMY12(event.capturedAtIst, "-")}${pending ? " (pending sync)" : ""}`
          : busy
          ? "Capturing..."
          : disabled
          ? disabledText
          : "Not captured"}
      </div>
      {checked ? (
        <div className="small ms-4">
          {event.latitude != null && event.longitude != null ? (
            <a href={locationMapLink(event.latitude, event.longitude)} target="_blank" rel="noreferrer">
              {gpsProofLabel(event.gpsProofType)} location
              {event.accuracy ? ` (accuracy ${Math.round(event.accuracy)} m)` : ""}
            </a>
          ) : (
            <span className="text-warning">Location not captured</span>
          )}
          {ODOMETER_EVENT_TYPES.includes(event.eventType) ? (
            <div>
              {event.odometerReading != null ? (
                <>
                  Odometer {Number(event.odometerReading).toLocaleString("en-IN")} km
                  {event.odometerPhotoUrl ? (
                    <>
                      {" - "}
                      <a href={event.odometerPhotoUrl} target="_blank" rel="noreferrer">photo</a>
                    </>
                  ) : null}
                </>
              ) : (
                <span className="text-warning">Odometer not captured</span>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function applyEvent(detail, event) {
  if (!detail || !event) return detail;
  const nextEvents = upsertEvent(detail.events || [], event);
  if (event.eventType === "DRIVER_REACHED_PICKUP") return { ...detail, driverReachedPickup: event, events: nextEvents };
  if (event.eventType === "TRIP_STARTED") return { ...detail, tripStarted: event, tripStatus: "ONGOING", events: nextEvents };
  if (event.eventType === "TRIP_ENDED") return { ...detail, tripEnded: event, events: nextEvents };
  if (event.eventType === "DAY_START" && event.placeKey === "__DAY__") return { ...detail, dayStart: event, events: nextEvents };
  if (event.eventType === "DAY_END" && event.placeKey === "__DAY__") return { ...detail, dayEnd: event, events: nextEvents };
  return {
    ...detail,
    events: nextEvents,
    places: (detail.places || []).map((place) => {
      if (place.key !== event.placeKey) return place;
      if (event.eventType === "PICKED_UP") return { ...place, pickedUp: event };
      if (event.eventType === "DROPPED") return { ...place, dropped: event };
      return place;
    }),
  };
}

function moveTripToOngoing(trips, bookingId) {
  const upcoming = trips?.upcoming || [];
  const ongoing = trips?.ongoing || [];
  const completed = trips?.completed || [];
  const trip = upcoming.find((item) => item.bookingId === bookingId);
  if (!trip) return trips;
  return {
    upcoming: upcoming.filter((item) => item.bookingId !== bookingId),
    ongoing: [{ ...trip, tripStatus: "ONGOING" }, ...ongoing.filter((item) => item.bookingId !== bookingId)],
    completed,
  };
}

function upsertEvent(events, event) {
  const key = `${event.eventType}:${event.placeKey}`;
  const filtered = events.filter((item) => `${item.eventType}:${item.placeKey}` !== key);
  return [...filtered, event];
}

function buildEventMap(detail) {
  const map = new Map();
  (detail?.events || []).forEach((event) => {
    map.set(`${event.eventType}:${event.placeKey}`, event);
  });
  if (detail?.dayStart) map.set("DAY_START:__DAY__", detail.dayStart);
  if (detail?.dayEnd) map.set("DAY_END:__DAY__", detail.dayEnd);
  return map;
}

function groupPlacesByDay(places) {
  const groups = [];
  const byKey = new Map();
  places.forEach((place) => {
    const key = place.dayKey || "day_0";
    if (!byKey.has(key)) {
      const group = {
        key,
        index: groups.length,
        label: place.dayLabel || `Day ${groups.length + 1}`,
        title: place.dayTitle || "",
        places: [],
      };
      groups.push(group);
      byKey.set(key, group);
    }
    byKey.get(key).places.push(place);
  });
  return groups;
}

function groupHotelsByDay(hotels) {
  const groups = [];
  const byKey = new Map();
  hotels.forEach((hotel) => {
    const dayNumber = hotel.dayNumber || 1;
    const key = `day_${dayNumber}`;
    if (!byKey.has(key)) {
      const group = {
        key,
        label: [hotel.dayLabel || `Day ${dayNumber}`, formatDateDMY(hotel.stayDate, "")].filter(Boolean).join(" - "),
        hotels: [],
      };
      groups.push(group);
      byKey.set(key, group);
    }
    byKey.get(key).hotels.push(hotel);
  });
  return groups;
}

function isDayCompleted(day, eventByKey, isLastDay) {
  if (isLastDay) {
    return day.places.every((place) => place.dropped && place.pickedUp);
  }
  return Boolean(eventByKey.get(`DAY_END:${day.key}`));
}

function readDriverSession(storageKey = "driverSession") {
  try {
    return JSON.parse(localStorage.getItem(storageKey) || "null");
  } catch {
    return null;
  }
}

function statusVariant(status) {
  if (status === "COMPLETED") return "success";
  if (status === "ONGOING") return "warning";
  if (status === "UPCOMING") return "primary";
  return "secondary";
}

function captureEventLocation(proofType = "EXACT") {
  if (!navigator.geolocation) return Promise.resolve({ gpsProofType: "MISSING" });
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => resolve({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        gpsProofType: proofType,
        gpsCapturedAt: new Date().toISOString(),
      }),
      () => resolve({ gpsProofType: "MISSING" }),
      { enableHighAccuracy: true, maximumAge: 30000, timeout: 12000 },
    );
  });
}

function hasLocation(location) {
  return location?.latitude != null && location?.longitude != null;
}

function deviceInfo() {
  return [navigator.userAgent, navigator.platform].filter(Boolean).join(" | ");
}

function gpsProofLabel(proofType) {
  if (proofType === "DELAYED") return "Delayed GPS";
  if (proofType === "EXACT" || !proofType) return "Captured GPS";
  return "GPS";
}

function locationMapLink(latitude, longitude) {
  return `https://www.google.com/maps?q=${encodeURIComponent(`${latitude},${longitude}`)}`;
}

export function usePwaInstallPrompt() {
  const [promptEvent, setPromptEvent] = useState(null);
  useEffect(() => {
    const onPrompt = (event) => {
      event.preventDefault();
      setPromptEvent(event);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);
  const install = useCallback(async () => {
    if (!promptEvent) return;
    promptEvent.prompt();
    await promptEvent.userChoice.catch(() => null);
    setPromptEvent(null);
  }, [promptEvent]);
  return { available: Boolean(promptEvent), install };
}
